// Implementação simulada de ApiClient, com latência e mutações em memória.
import { ApiRequestError, type ApiClient } from "../client";
import type * as T from "../types";
import { getLocale } from "../../i18n/core";
import {
  addOrg, assignments, closeOrg, HOME_ORG, listDir, newHex, orgData, organizations, orgPools, platformPools, poolTotals, purgeOrg,
  renameOrg, setTrialEnd, reopenOrg, storageSummary, type OrgData,
} from "./data";

const DAY = 86_400_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const latency = () => sleep(160 + Math.random() * 260);
const iso = (ms: number) => new Date(ms).toISOString();

function need(orgId: string): OrgData {
  const d = orgData(orgId);
  if (!d) throw new ApiRequestError(404, "org_not_found", "Organização não encontrada.");
  return d;
}

function needMachine(d: OrgData, machineId: string): T.MachineDetail {
  const m = d.machines.find((x) => x.id === machineId);
  if (!m) throw new ApiRequestError(404, "machine_not_found", "Máquina não encontrada nesta organização.");
  return m;
}

const clone = <V,>(v: V): V => structuredClone(v);

function toOrgDetail(org: T.Organization): T.OrganizationDetail {
  return {
    id: org.id, name: org.name, kind: org.kind, parentId: org.parentId, quotaBytes: org.quotaBytes, poolName: "Datacenter São Paulo",
    closedAt: org.closedAt, purgeAfter: org.purgeAfter, purgeError: org.purgeError, trialEndsAt: org.trialEndsAt,
  };
}

// Mesmo crivo do servidor para nome de banco, de usuário e de host (ADR 0023):
// o que passa aqui é o que viraria argumento da ferramenta de dump.
const DB_NAME = /^[A-Za-z0-9_][A-Za-z0-9_$.-]{0,62}$/;

function checkDatabaseInput(input: T.DatabaseInput, list: T.DatabaseInstance[], selfId: string) {
  const name = input.name.trim();
  if (!name || name.length > 80) throw new ApiRequestError(422, "invalid_database", "Dê um nome à instância (até 80 caracteres).");
  if (list.some((x) => x.id !== selfId && x.name.toLowerCase() === name.toLowerCase())) {
    throw new ApiRequestError(409, "database_name_taken", "Já existe uma instância com este nome nesta máquina.");
  }
  if (!input.all && input.databases.length === 0) throw new ApiRequestError(422, "invalid_database", "Escolha todos os bancos ou liste de 1 a 500 nomes.");
  if (input.databases.some((n) => !DB_NAME.test(n))) {
    throw new ApiRequestError(422, "invalid_database", "Nome de banco inválido: use letras, números, _ $ . e -, e liste de 1 a 500 nomes.");
  }
  if (input.auth === "password" && !DB_NAME.test(input.user ?? "")) throw new ApiRequestError(422, "invalid_database", "Informe o usuário do banco.");
}

/** Enfileira teste ou backup e, como o agente faria, conclui em alguns segundos. */
function startDatabaseJob(d: OrgData, orgId: string, machineId: string, databaseId: string, kind: "database" | "dbtest"): T.Job {
  const m = needMachine(d, machineId);
  const inst = (d.databases.get(machineId) ?? []).find((x) => x.id === databaseId);
  if (!inst) throw new ApiRequestError(404, "database_not_found", "Instância de banco não encontrada.");
  if (m.status === "revoked") throw new ApiRequestError(409, "machine_revoked", "Máquina revogada não pode fazer backup. Registre-a novamente.");
  if (inst.activeJob) throw new ApiRequestError(409, "database_busy", "Já existe um teste ou backup desta instância em andamento.");
  const test = kind === "dbtest";
  const job: T.Job = {
    id: `${orgId}:job-${newHex(8)}`, kind, status: "queued", machineId: m.id, hostname: m.hostname, startedAt: iso(Date.now()), endedAt: null, bytes: 0, progress: 0,
    message: test ? "Aguardando o agente testar a instância" : "Aguardando o agente iniciar o backup do banco",
    messageCode: test ? "job.dbtest.queued" : "job.database.queued", databaseId, shareId: null,
  };
  d.jobs.unshift(job);
  inst.activeJob = kind;
  if (test) {
    // Como o agente faria: cada passo do checklist vira a mensagem do trabalho.
    const pg = inst.engine === "postgresql";
    const lines = [`Ferramenta ${pg ? "pg_dump 16.4" : "mysqldump 8.0.39"} encontrada.`, `Conectado: ${pg ? "PostgreSQL 16.4" : "MySQL 8.0.39"}.`];
    lines.forEach((line, i) => setTimeout(() => { if (job.status === "queued") { job.status = "running"; job.message = line; } }, 600 * (i + 1)));
  }
  setTimeout(() => {
    inst.activeJob = "";
    job.status = "succeeded";
    job.endedAt = iso(Date.now());
    if (test) {
      const pg = inst.engine === "postgresql";
      const names = inst.all || inst.databases.length === 0 ? ["erp", "fiscal"] : inst.databases;
      inst.lastTest = {
        ok: true, code: "db.test.ok", message: "Conexão, ferramenta e permissões estão certas.", version: pg ? "16.4" : "8.0.39", flavor: pg ? "" : "mysql",
        tool: pg ? "pg_dump" : "mysqldump", toolVersion: pg ? "16.4" : "8.0.39", role: "", warnings: [], at: iso(Date.now()),
        databases: names.map((n, i) => ({ name: n, sizeBytes: (i + 2) * 1_610_612_736 })),
        steps: [
          { code: "db.step.tool_found", message: `Ferramenta ${pg ? "pg_dump 16.4" : "mysqldump 8.0.39"} encontrada.`, status: "ok" },
          { code: "db.step.connected", message: `Conectado: ${pg ? "PostgreSQL 16.4" : "MySQL 8.0.39"}.`, status: "ok" },
          { code: "db.step.databases", message: `${names.length} bancos vistos.`, status: "ok" },
        ],
      };
      job.message = "Teste da instância aprovado";
      job.messageCode = "job.dbtest.passed";
      return;
    }
    const pg = inst.engine === "postgresql";
    const dbs = (inst.lastTest?.databases ?? []).length ? inst.lastTest?.databases ?? [] : inst.databases.map((n) => ({ name: n, sizeBytes: 1_073_741_824 }));
    const streams: T.SnapshotDatabase[] = dbs.map((x) => ({ name: x.name, stream: `${x.name}.${pg ? "pgdump" : "sql"}`, format: pg ? "pg_custom" : "sql", sizeBytes: Math.round(x.sizeBytes * 0.57) }));
    const total = streams.reduce((acc, x) => acc + x.sizeBytes, 0);
    const snaps = d.snapshots.get(machineId) ?? [];
    snaps.unshift({
      id: newHex(32), machineId, sourcePath: `db://${inst.engine}/${inst.id}`, startedAt: job.startedAt, endedAt: job.endedAt, status: "complete", totalBytes: total,
      addedBytes: Math.round(total * 0.02), fileCount: streams.length + 1, retention: ["latest"], poolId: "", immutableUntil: null, trigger: "manual",
      kind: "database", volumes: [], databaseId: inst.id, databases: streams, shareId: null, errors: [], errorTotal: 0, errorGroups: [], catalog: "none",
    });
    d.snapshots.set(machineId, snaps);
    job.bytes = total;
    job.message = `${streams.length} banco(s) protegidos`;
    job.messageCode = "job.database.done";
  }, 2_000);
  return clone(job);
}

// Mesmo crivo do servidor para o compartilhamento (ADR 0030).
const HOST = /^(\[[0-9A-Fa-f:.]+\]|[A-Za-z0-9]([A-Za-z0-9-]{0,62}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,62}[A-Za-z0-9])?)*)$/;
const SHARE_NAME = /^[^\\/:*?"<>|]{1,80}$/;
const hasControl = (s: string) => [...s].some((ch) => ch.charCodeAt(0) < 32);

function checkShareInput(d: OrgData, input: T.ShareInput, list: T.Share[], selfId: string) {
  const name = input.name.trim();
  if (!name || name.length > 80) throw new ApiRequestError(422, "invalid_share", "Dê um nome ao compartilhamento (até 80 caracteres).");
  if (list.some((x) => x.id !== selfId && x.name.toLowerCase() === name.toLowerCase())) {
    throw new ApiRequestError(409, "share_name_taken", "Esta máquina já tem um compartilhamento com este nome.");
  }
  if (!HOST.test(input.host.trim())) throw new ApiRequestError(422, "invalid_share", "Servidor inválido. Use um nome de host, um IPv4 ou um IPv6 entre colchetes.");
  if (!SHARE_NAME.test(input.share.trim()) || hasControl(input.share)) throw new ApiRequestError(422, "invalid_share", "Nome do compartilhamento inválido.");
  const path = (input.path ?? "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
  if (path.split("/").some((seg) => seg === "..")) throw new ApiRequestError(422, "invalid_share", "Pasta inicial inválida: sem \"..\".");
  if (input.credentialId && !d.credentials.some((c) => c.id === input.credentialId)) {
    throw new ApiRequestError(422, "invalid_share", "Credencial inexistente nesta organização.");
  }
  return { name, path };
}

function shareUNC(host: string, share: string, path: string): string {
  return `\\\\${host}\\${share}${path ? `\\${path.replace(/\//g, "\\")}` : ""}`;
}

/** Enfileira teste ou backup do compartilhamento e, como o agente faria, conclui em alguns segundos. */
function startShareJob(d: OrgData, orgId: string, machineId: string, shareId: string, kind: "share" | "sharetest"): T.Job {
  const m = needMachine(d, machineId);
  const sh = (d.shares.get(machineId) ?? []).find((x) => x.id === shareId);
  if (!sh) throw new ApiRequestError(404, "share_not_found", "Compartilhamento não encontrado nesta máquina.");
  if (m.status === "revoked") throw new ApiRequestError(409, "machine_revoked", "Máquina revogada não pode fazer backup. Registre-a novamente.");
  if (sh.activeJob) throw new ApiRequestError(409, "share_busy", "Este compartilhamento já tem um backup ou um teste na fila ou em andamento.");
  const test = kind === "sharetest";
  const job: T.Job = {
    id: `${orgId}:job-${newHex(8)}`, kind, status: "queued", machineId: m.id, hostname: m.hostname, startedAt: iso(Date.now()), endedAt: null, bytes: 0, progress: 0,
    message: test ? "Aguardando o agente testar o compartilhamento" : "Aguardando o agente iniciar o backup do compartilhamento",
    messageCode: test ? "job.sharetest.queued" : "job.share.queued", databaseId: null, shareId,
  };
  d.jobs.unshift(job);
  sh.activeJob = kind;
  const user = d.credentials.find((c) => c.id === sh.credentialId)?.username ?? "anonymous";
  const steps: T.ShareTest["steps"] = [
    { code: "share.step.resolve", message: `Resolvendo o nome ${sh.host}`, status: "ok" },
    { code: "share.step.connect", message: `Conectando a ${sh.host} pela porta 445`, status: "ok" },
    { code: "share.step.auth", message: `Autenticando como ${user}`, status: "ok" },
    { code: "share.step.mount", message: `Abrindo o compartilhamento ${sh.share}`, status: "ok" },
    { code: "share.step.list", message: `Listando a pasta ${sh.path || "/"}`, status: "ok" },
    { code: "share.step.estimate", message: "Estimando o volume: 18.420 itens, 96,3 GB", status: "ok" },
  ];
  if (test) {
    // Como o agente faria: cada passo do checklist vira a mensagem do trabalho.
    steps.forEach((st, i) => setTimeout(() => { if (job.status !== "succeeded") { job.status = "running"; job.message = st.message; } }, 400 * (i + 1)));
  }
  setTimeout(() => {
    sh.activeJob = "";
    job.status = "succeeded";
    job.endedAt = iso(Date.now());
    if (test) {
      sh.lastTest = {
        ok: true, code: "share.test.ok", message: "Conexão, credencial e pasta estão certas (SMB 3.1.1).", dialect: "3.1.1", signed: true, encrypted: false,
        entries: 18_420, bytes: 96_300_000_000, warnings: sh.credentialId ? [] : [{ code: "share.warn.anonymous", message: "Sem credencial: acesso anônimo ao compartilhamento." }],
        steps, at: iso(Date.now()),
      };
      job.message = "Teste do compartilhamento aprovado";
      job.messageCode = "job.sharetest.passed";
      return;
    }
    const total = 96_300_000_000;
    const snaps = d.snapshots.get(machineId) ?? [];
    snaps.unshift({
      id: newHex(32), machineId, sourcePath: sh.unc, startedAt: job.startedAt, endedAt: job.endedAt, status: "complete", totalBytes: total,
      addedBytes: Math.round(total * 0.01), fileCount: 18_420, retention: ["latest"], poolId: "", immutableUntil: null, trigger: "manual",
      kind: "share", volumes: [], databaseId: null, databases: [], shareId: sh.id, errors: [], errorTotal: 0, errorGroups: [], catalog: "done",
    });
    d.snapshots.set(machineId, snaps);
    job.bytes = total;
    job.message = "Backup do compartilhamento concluído: 96,3 GB";
    job.messageCode = "job.share.done";
  }, 3_000);
  return clone(job);
}

const severity: Record<T.ProtectionStatus, number> = { failed: 0, stale: 1, never: 2, revoked: 3, protected: 4 };

let session: T.Session | null = null;
const SESSION_KEY = "ebk-mock-session";
try {
  if (sessionStorage.getItem(SESSION_KEY)) session = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? "null");
} catch {
  session = null;
}

function saveSession() {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* armazenamento indisponível: sessão só em memória */
  }
}

function needSession(): T.Session {
  if (!session) throw new ApiRequestError(401, "no_session", "Sessão expirada. Entre novamente.");
  return session;
}

/** Rotas /admin exigem administrador da plataforma com sessão (ADR 0021). */
function needPlatformAdmin(): T.Session {
  const s = needSession();
  if (!s.user.platformAdmin) throw new ApiRequestError(403, "forbidden", "Só o administrador da plataforma gere pools e atribuições.");
  return s;
}

function needPool(poolId: string): T.StoragePool {
  const p = platformPools().find((x) => x.id === poolId);
  if (!p) throw new ApiRequestError(404, "pool_not_found", "Pool de armazenamento não encontrado.");
  return p;
}

/** Pool com uso, quota atribuída e número de organizações calculados. */
function withTotals(pool: T.StoragePool): T.StoragePool {
  return { ...clone(pool), ...poolTotals(pool.id) };
}

function locationOf(input: T.PoolInput): string {
  const s = input.settings;
  if (s.s3) return `${s.s3.endpoint} / ${s.s3.bucket}${s.s3.prefix ? `/${s.s3.prefix}` : ""}`;
  if (s.sftp) return `sftp://${s.sftp.host}${s.sftp.port && s.sftp.port !== 22 ? `:${s.sftp.port}` : ""}${s.sftp.path}`;
  if (s.interserver) return `${s.interserver.url}/${s.interserver.path} · ${s.interserver.username}`;
  if (s.webdav) return s.webdav.url;
  return "—";
}

/**
 * Desafios de login em andamento. No mock, entrar com o e-mail de um membro
 * sem autenticador (auditoria@contabilexemplo.com.br, ou alguém redefinido em
 * Configurações) demonstra o cadastro do autenticador no primeiro acesso.
 */
const challenges = new Map<string, { email: string; enroll: boolean }>();

const MOCK_TOTP_SECRET = "KRSXG5CTMVRXEZLUKN2XAZLSKNSWG4TF";

function findUser(email: string): T.User | undefined {
  return need(HOME_ORG).users.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
}

// Troca de e-mail pendente; fica na aba para sobreviver à navegação até /confirm-email.
const EMAIL_CHANGE_KEY = "ebk-mock-email-change";
type PendingEmail = { userId: string; email: string; expiresAt: number };

function readPendingEmail(): PendingEmail | null {
  try {
    return JSON.parse(sessionStorage.getItem(EMAIL_CHANGE_KEY) ?? "null") as PendingEmail | null;
  } catch {
    return null;
  }
}

function writePendingEmail(p: PendingEmail | null) {
  try {
    if (p) sessionStorage.setItem(EMAIL_CHANGE_KEY, JSON.stringify(p));
    else sessionStorage.removeItem(EMAIL_CHANGE_KEY);
  } catch {
    /* sem armazenamento: o link simulado não funciona depois de recarregar */
  }
}

export function createMockClient(): ApiClient {
  return {
    async login(email, password) {
      await latency();
      if (!email.includes("@") || password.length < 8) {
        throw new ApiRequestError(401, "invalid_credentials", "E-mail ou senha incorretos.");
      }
      const challengeId = newHex(16);
      const member = findUser(email);
      const enroll = member?.totpEnabled === false;
      challenges.set(challengeId, { email, enroll });
      if (!enroll) return { challengeId };
      const address = member?.email ?? email;
      return {
        challengeId,
        totpSetup: {
          email: address,
          secret: MOCK_TOTP_SECRET,
          otpauthUri: `otpauth://totp/E-BACKUP:${encodeURIComponent(address)}?secret=${MOCK_TOTP_SECRET}&issuer=E-BACKUP`,
        },
      };
    },

    async verifyTotp(challengeId, code) {
      await latency();
      if (!/^\d{6}$/.test(code)) throw new ApiRequestError(401, "invalid_totp", "Código inválido. Use os 6 dígitos do aplicativo autenticador.");
      const d = need(HOME_ORG);
      const challenge = challenges.get(challengeId);
      challenges.delete(challengeId);
      const user = (challenge && findUser(challenge.email)) ?? d.users[0];
      if (!user) throw new ApiRequestError(500, "no_user", "Usuário de exemplo ausente.");
      // O primeiro código válido confirma o autenticador cadastrado no login.
      if (challenge?.enroll) user.totpEnabled = true;
      session = { user: clone(user), homeOrgId: HOME_ORG, csrfToken: "mock", expiresAt: iso(Date.now() + 30 * 60_000) };
      saveSession();
      return clone(session);
    },

    async logout() {
      await sleep(80);
      session = null;
      saveSession();
    },

    async requestPasswordReset() {
      await latency();
    },

    // Teste gratuito (ADR 0038): os limites são os padrões do pool de teste.
    async getTrialInfo() {
      await latency();
      const site = "https://www.e-backup.cloud";
      const paths: Record<string, [string, string]> = { "en-US": ["/en/terms/", "/en/privacy/"], es: ["/es/terminos/", "/es/privacidad/"] };
      const [terms, privacy] = paths[getLocale()] ?? ["/termos/", "/privacidade/"];
      return { available: true, days: 30, quotaBytes: 100e9, machineLimit: 5, graceDays: 15, termsVersion: "2026-10-06", termsUrl: site + terms, privacyUrl: site + privacy };
    },

    async trialSignup(input) {
      await latency();
      const name = (input.name ?? "").trim();
      const company = (input.company ?? "").trim();
      if (name.length < 2 || name.length > 120) throw new ApiRequestError(422, "invalid_trial", "Informe seu nome (2 a 120 caracteres).");
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.email ?? "") || input.email.length > 254) throw new ApiRequestError(422, "invalid_trial", "Informe um e-mail válido.");
      if (company.length < 2 || company.length > 120) throw new ApiRequestError(422, "invalid_trial", "Informe o nome da empresa (2 a 120 caracteres).");
      if ((input.phone ?? "").length > 30) throw new ApiRequestError(422, "invalid_trial", "Telefone longo demais.");
      if (input.acceptTerms !== true) throw new ApiRequestError(422, "terms_not_accepted", "Para continuar, aceite os termos de uso e a política de privacidade.");
    },

    async confirmPasswordReset(input) {
      await latency();
      if (!input.token.startsWith("ebr1_")) throw new ApiRequestError(404, "invalid_reset", "Este link de redefinição é inválido, já foi usado ou expirou. Peça outro.");
    },

    async getSession() {
      await sleep(60);
      if (!session) throw new ApiRequestError(401, "no_session", "Sessão expirada. Entre novamente.");
      return clone(session);
    },

    async changePassword(input) {
      await latency();
      if (input.newPassword.length < 12) throw new ApiRequestError(422, "weak_password", "A senha precisa ter de 12 a 256 caracteres.");
      if (!/^\d{6}$/.test(input.code)) throw new ApiRequestError(422, "invalid_totp", "Código inválido ou já usado.");
    },

    async updateProfile(input) {
      await latency();
      const s = needSession();
      const name = input.name.trim();
      if (!name || name.length > 120) throw new ApiRequestError(422, "invalid_input", "Informe um nome de 1 a 120 caracteres.");
      s.user.name = name;
      const member = need(HOME_ORG).users.find((u) => u.id === s.user.id);
      if (member) member.name = name;
      saveSession();
      return { name };
    },

    async changeEmail(input) {
      await latency();
      const s = needSession();
      const email = input.newEmail.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || email.length > 254) throw new ApiRequestError(422, "invalid_input", "Informe um e-mail válido.");
      if (email.toLowerCase() === s.user.email.toLowerCase()) throw new ApiRequestError(422, "invalid_input", "O novo e-mail é igual ao atual.");
      if (input.currentPassword.length < 8) throw new ApiRequestError(422, "invalid_current_password", "Senha atual incorreta, ou conta temporariamente bloqueada.");
      if (!/^\d{6}$/.test(input.code)) throw new ApiRequestError(422, "invalid_totp.code", "Código inválido ou já usado.");
      // Como um servidor com e-mail configurado: um pedido novo substitui o anterior.
      writePendingEmail({ userId: s.user.id, email, expiresAt: Date.now() + 3600_000 });
      return { status: "pending", email };
    },

    async confirmEmailChange(token) {
      await latency();
      const pending = readPendingEmail();
      if (!token.startsWith("ebe1_") || !pending || pending.expiresAt < Date.now()) {
        throw new ApiRequestError(422, "invalid_email_change", "Este link de confirmação é inválido, já foi usado ou expirou. Peça a troca de novo.");
      }
      const users = need(HOME_ORG).users;
      if (users.some((u) => u.id !== pending.userId && u.email.toLowerCase() === pending.email.toLowerCase())) {
        writePendingEmail(null);
        throw new ApiRequestError(409, "email_taken", "Este e-mail já é usado por outra conta. O e-mail não foi trocado.");
      }
      writePendingEmail(null);
      const member = users.find((u) => u.id === pending.userId);
      if (member) member.email = pending.email;
      if (session?.user.id === pending.userId) {
        session.user.email = pending.email;
        saveSession();
      }
      return { status: "changed", email: pending.email };
    },

    async lookupInvitation(token) {
      await latency();
      if (!token.startsWith("ebi1_")) throw new ApiRequestError(404, "invalid_invitation", "Este convite é inválido, já foi usado, revogado ou expirou.");
      return { orgName: "Serra Azul Contabilidade", email: "nova.pessoa@serraazul.com.br", name: "Nova Pessoa", role: "operator", expiresAt: iso(Date.now() + 70 * 3600_000), existingAccount: false };
    },

    async beginInvitationSignup(input) {
      await latency();
      if (input.password.length < 12) throw new ApiRequestError(422, "weak_password", "A senha precisa ter de 12 a 256 caracteres.");
      return { email: "nova.pessoa@serraazul.com.br", secret: "JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP", otpauthUri: "otpauth://totp/E-BACKUP:nova.pessoa%40serraazul.com.br?secret=JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP&issuer=E-BACKUP" };
    },

    async activateInvitation(input) {
      return this.verifyTotp(input.token, input.code);
    },

    async joinInvitation() {
      await latency();
      return { orgId: HOME_ORG };
    },

    async updatePreferences(input) {
      await sleep(60);
      needSession().user.locale = input.locale;
      saveSession();
      return { locale: input.locale };
    },

    async listOrganizations() {
      await latency();
      return organizations();
    },

    async getOverview(orgId) {
      await latency();
      const d = need(orgId);
      const count = (s: T.ProtectionStatus) => d.machines.filter((m) => m.status === s).length;
      return clone({
        counts: {
          total: d.machines.length,
          protected: count("protected"),
          stale: count("stale"),
          failed: count("failed"),
          never: count("never"),
          revoked: count("revoked"),
          online: d.machines.filter((m) => m.online).length,
        },
        attention: d.machines
          .filter((m) => m.status !== "protected" && m.status !== "revoked")
          .sort((a, b) => severity[a.status] - severity[b.status]),
        usage: { quotaBytes: d.usage.quotaBytes, protectedBytes: d.usage.protectedBytes },
        pools: d.pools,
        recentJobs: d.jobs.slice(0, 8),
      });
    },

    async listMachines(orgId, filter) {
      await latency();
      const d = need(orgId);
      const q = filter?.q?.trim().toLowerCase();
      return clone(
        d.machines.filter(
          (m) =>
            (!filter?.status || m.status === filter.status) &&
            (!filter?.groupId || m.groupId === filter.groupId) &&
            (!q || m.hostname.toLowerCase().includes(q)),
        ),
      );
    },

    async getMachine(orgId, machineId) {
      await latency();
      return clone(needMachine(need(orgId), machineId));
    },

    async startBackup(orgId, machineId) {
      await latency();
      const d = need(orgId);
      const m = needMachine(d, machineId);
      if (m.status === "revoked") throw new ApiRequestError(409, "machine_revoked", "Máquina revogada não pode fazer backup. Registre-a novamente.");
      if (!m.online) throw new ApiRequestError(409, "machine_offline", `${m.hostname} está offline. O backup começa assim que o agente se conectar.`);
      const job: T.Job = {
        id: `${orgId}:job-${newHex(8)}`, kind: "backup", status: "queued", machineId: m.id, hostname: m.hostname,
        startedAt: iso(Date.now()), endedAt: null, bytes: 0, progress: 0, message: "Aguardando o agente iniciar o snapshot", messageCode: "job.backup.queued", databaseId: null, shareId: null,
      };
      d.jobs.unshift(job);
      d.audit.unshift({ id: newHex(8), at: job.startedAt, actor: session?.user.name ?? "—", action: "Iniciou backup manual", actionCode: "", target: m.hostname, ip: "177.92.14.203" });
      return clone(job);
    },

    async startImage(orgId, machineId, disks) {
      await latency();
      const d = need(orgId);
      const m = needMachine(d, machineId);
      if (m.status === "revoked") throw new ApiRequestError(409, "machine_revoked", "Máquina revogada não pode fazer backup. Registre-a novamente.");
      const inventory = d.disks.get(machineId)?.disks ?? [];
      if (inventory.length === 0) {
        throw new ApiRequestError(409, "image_unsupported", "Esta máquina não faz backup de imagem.");
      }
      const job: T.Job = {
        id: `${orgId}:job-${newHex(8)}`, kind: "image", status: "queued", machineId: m.id, hostname: m.hostname,
        startedAt: iso(Date.now()), endedAt: null, bytes: 0, progress: 0,
        message: "Aguardando o agente iniciar a imagem de disco", messageCode: "job.image.queued", databaseId: null, shareId: null,
      };
      d.jobs.unshift(job);
      d.audit.unshift({ id: newHex(8), at: job.startedAt, actor: session?.user.name ?? "—", action: "Iniciou imagem de disco manual", actionCode: "", target: m.hostname, ip: "177.92.14.203" });
      void disks;
      return clone(job);
    },

    async listDisks(orgId, machineId) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      return clone(d.disks.get(machineId) ?? { disks: [], updatedAt: null });
    },

    async listDatabases(orgId, machineId) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      return clone(d.databases.get(machineId) ?? []);
    },

    async foundDatabases(orgId, machineId) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      return clone(d.foundDatabases.get(machineId) ?? { found: [], updatedAt: null });
    },

    async createDatabase(orgId, machineId, input) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      const list = d.databases.get(machineId) ?? [];
      checkDatabaseInput(input, list, "");
      if (input.auth === "password" && !input.password) throw new ApiRequestError(422, "invalid_database", "Informe a senha do usuário do banco.");
      const at = iso(Date.now());
      const created: T.DatabaseInstance = {
        id: `${machineId}:db-${newHex(4)}`, name: input.name.trim(), engine: input.engine, host: input.host ?? "", port: input.port ?? 0, socket: input.socket ?? "",
        auth: input.auth, user: input.user ?? "", osUser: input.osUser ?? "", databases: input.databases, all: input.all, schedule: input.schedule ?? null,
        enabled: input.enabled ?? true, hasSecret: input.auth === "password", lastTest: null, activeJob: "", createdAt: at, updatedAt: at,
        sid: input.sid ?? "", mode: input.mode ?? "", stagingDir: input.stagingDir ?? "", deleteArchivedLogs: input.deleteArchivedLogs ?? false,
      };
      d.databases.set(machineId, [...list, created]);
      return clone(created);
    },

    async updateDatabase(orgId, machineId, databaseId, input) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      const list = d.databases.get(machineId) ?? [];
      const cur = list.find((x) => x.id === databaseId);
      if (!cur) throw new ApiRequestError(404, "database_not_found", "Instância de banco não encontrada.");
      checkDatabaseInput(input, list, databaseId);
      const hasSecret = input.auth === "password" && (input.password === undefined ? cur.hasSecret : input.password !== "");
      if (input.auth === "password" && !hasSecret) throw new ApiRequestError(422, "invalid_database", "Informe a senha do usuário do banco.");
      Object.assign(cur, {
        name: input.name.trim(), host: input.host ?? "", port: input.port ?? 0, socket: input.socket ?? "", auth: input.auth, user: input.user ?? "",
        osUser: input.osUser ?? "", databases: input.databases, all: input.all, schedule: input.schedule ?? null, enabled: input.enabled ?? true,
        hasSecret, lastTest: null, updatedAt: iso(Date.now()),
        sid: input.sid ?? "", mode: input.mode ?? "", stagingDir: input.stagingDir ?? "", deleteArchivedLogs: input.deleteArchivedLogs ?? false,
      });
      return clone(cur);
    },

    async deleteDatabase(orgId, machineId, databaseId) {
      await latency();
      const d = need(orgId);
      const list = d.databases.get(machineId) ?? [];
      if (!list.some((x) => x.id === databaseId)) throw new ApiRequestError(404, "database_not_found", "Instância de banco não encontrada.");
      d.databases.set(machineId, list.filter((x) => x.id !== databaseId));
    },

    async testDatabase(orgId, machineId, databaseId) {
      await latency();
      return startDatabaseJob(need(orgId), orgId, machineId, databaseId, "dbtest");
    },

    async startDatabaseBackup(orgId, machineId, databaseId) {
      await latency();
      return startDatabaseJob(need(orgId), orgId, machineId, databaseId, "database");
    },

    // Compartilhamentos SMB (ADR 0030).
    async listShares(orgId, machineId) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      return clone(d.shares.get(machineId) ?? []);
    },

    async createShare(orgId, machineId, input) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      const list = d.shares.get(machineId) ?? [];
      const { name, path } = checkShareInput(d, input, list, "");
      const at = iso(Date.now());
      const created: T.Share = {
        id: `${machineId}:share-${newHex(4)}`, name, host: input.host.trim(), share: input.share.trim(), path, unc: shareUNC(input.host.trim(), input.share.trim(), path),
        credentialId: input.credentialId ?? "", exclude: input.exclude ?? [], schedule: input.schedule ?? null, enabled: input.enabled ?? true,
        lastTest: null, activeJob: "", createdAt: at, updatedAt: at,
      };
      d.shares.set(machineId, [...list, created]);
      const cred = d.credentials.find((c) => c.id === created.credentialId);
      if (cred) cred.usedBy++;
      return clone(created);
    },

    async updateShare(orgId, machineId, shareId, input) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      const list = d.shares.get(machineId) ?? [];
      const cur = list.find((x) => x.id === shareId);
      if (!cur) throw new ApiRequestError(404, "share_not_found", "Compartilhamento não encontrado nesta máquina.");
      const { name, path } = checkShareInput(d, input, list, shareId);
      const before = d.credentials.find((c) => c.id === cur.credentialId);
      if (before) before.usedBy--;
      Object.assign(cur, {
        name, host: input.host.trim(), share: input.share.trim(), path, unc: shareUNC(input.host.trim(), input.share.trim(), path),
        credentialId: input.credentialId ?? "", exclude: input.exclude ?? [], schedule: input.schedule ?? null, enabled: input.enabled ?? true, updatedAt: iso(Date.now()),
      });
      const after = d.credentials.find((c) => c.id === cur.credentialId);
      if (after) after.usedBy++;
      return clone(cur);
    },

    async deleteShare(orgId, machineId, shareId) {
      await latency();
      const d = need(orgId);
      const list = d.shares.get(machineId) ?? [];
      const cur = list.find((x) => x.id === shareId);
      if (!cur) throw new ApiRequestError(404, "share_not_found", "Compartilhamento não encontrado nesta máquina.");
      const cred = d.credentials.find((c) => c.id === cur.credentialId);
      if (cred) cred.usedBy--;
      d.shares.set(machineId, list.filter((x) => x.id !== shareId));
    },

    async testShare(orgId, machineId, shareId) {
      await latency();
      return startShareJob(need(orgId), orgId, machineId, shareId, "sharetest");
    },

    async startShareBackup(orgId, machineId, shareId) {
      await latency();
      return startShareJob(need(orgId), orgId, machineId, shareId, "share");
    },

    // Catálogo de credenciais (ADR 0030): só administrador; o segredo nunca volta.
    async listCredentials(orgId) {
      await latency();
      needSession();
      return clone(need(orgId).credentials);
    },

    async createCredential(orgId, input) {
      await latency();
      const d = need(orgId);
      const name = input.name.trim();
      if (!name || name.length > 80) throw new ApiRequestError(422, "invalid_credential", "O nome da credencial deve ter de 1 a 80 caracteres.");
      if (d.credentials.some((c) => c.name.toLowerCase() === name.toLowerCase())) throw new ApiRequestError(409, "credential_name_taken", "Já existe uma credencial com este nome.");
      if (!input.secret) throw new ApiRequestError(422, "invalid_credential", "Informe a senha (até 256 caracteres, sem quebra de linha).");
      const at = iso(Date.now());
      const created: T.Credential = {
        id: `${orgId}:cred-${newHex(4)}`, name, kind: "smb", username: (input.username ?? "").trim(), domain: (input.domain ?? "").trim(), usedBy: 0,
        createdBy: needSession().user.name, createdAt: at, updatedAt: at, lastUsedAt: null,
      };
      d.credentials.push(created);
      return clone(created);
    },

    async updateCredential(orgId, credentialId, input) {
      await latency();
      const d = need(orgId);
      const cur = d.credentials.find((c) => c.id === credentialId);
      if (!cur) throw new ApiRequestError(404, "credential_not_found", "Credencial não encontrada.");
      const name = input.name.trim();
      if (!name || name.length > 80) throw new ApiRequestError(422, "invalid_credential", "O nome da credencial deve ter de 1 a 80 caracteres.");
      if (d.credentials.some((c) => c.id !== credentialId && c.name.toLowerCase() === name.toLowerCase())) throw new ApiRequestError(409, "credential_name_taken", "Já existe uma credencial com este nome.");
      Object.assign(cur, { name, username: (input.username ?? "").trim(), domain: (input.domain ?? "").trim(), updatedAt: iso(Date.now()) });
      return clone(cur);
    },

    async deleteCredential(orgId, credentialId) {
      await latency();
      const d = need(orgId);
      const cur = d.credentials.find((c) => c.id === credentialId);
      if (!cur) throw new ApiRequestError(404, "credential_not_found", "Credencial não encontrada.");
      if (cur.usedBy > 0) throw new ApiRequestError(409, "credential_in_use", "Esta credencial está em uso por um compartilhamento. Troque a credencial dele antes de apagar.");
      d.credentials = d.credentials.filter((c) => c.id !== credentialId);
    },

    async updateMachine(orgId, machineId, input) {
      await latency();
      const d = need(orgId);
      const m = needMachine(d, machineId);
      if (input.groupId) m.groupId = input.groupId;
      if (input.policyId) m.policyId = input.policyId;
      if (input.imageDisks) m.imageDisks = [...input.imageDisks];
      return clone(m);
    },

    async revokeMachine(orgId, machineId) {
      await latency();
      const d = need(orgId);
      const m = needMachine(d, machineId);
      m.status = "revoked";
      m.online = false;
      m.lastError = null;
      d.audit.unshift({ id: newHex(8), at: iso(Date.now()), actor: session?.user.name ?? "—", action: "Revogou máquina", actionCode: "", target: m.hostname, ip: "177.92.14.203" });
      return clone(m);
    },

    async listSnapshots(orgId, machineId) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      return clone(d.snapshots.get(machineId) ?? []);
    },

    async snapshotErrors(orgId, snapshotId) {
      await latency();
      const d = need(orgId);
      for (const list of d.snapshots.values()) {
        const s = list.find((x) => x.id === snapshotId);
        if (s) return { total: s.errorTotal, listed: s.errors.length, groups: clone(s.errorGroups), entries: clone(s.errors) };
      }
      throw new ApiRequestError(404, "snapshot_not_found", "Ponto de restauração não encontrado.");
    },

    async browseSnapshot(orgId, snapshotId, path) {
      await sleep(120 + Math.random() * 160);
      const d = need(orgId);
      let root = "/";
      let sep = "/";
      for (const [mid, snaps] of d.snapshots) {
        const s = snaps.find((x) => x.id === snapshotId);
        if (s) {
          root = s.sourcePath;
          sep = d.machines.find((m) => m.id === mid)?.os === "windows" ? "\\" : "/";
        }
      }
      const full = path ? root + sep + path.split("/").join(sep) : root;
      return listDir(full, snapshotId, Date.now());
    },

    // Catálogo (ADR 0036): a simulação anda três níveis da árvore do ponto
    // mais recente e filtra pelo nome.
    async searchCatalog(orgId, machineId, q) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      const term = q.trim().toLowerCase();
      const s = (d.snapshots.get(machineId) ?? []).find((x) => x.kind === "files" || x.kind === "share");
      if (!term || !s) return [];
      const sep = d.machines.find((m) => m.id === machineId)?.os === "windows" ? "\\" : "/";
      const hits: T.CatalogHit[] = [];
      const walk = (rel: string, depth: number) => {
        const full = rel ? s.sourcePath + sep + rel.split("/").join(sep) : s.sourcePath;
        for (const e of listDir(full, s.id, Date.now())) {
          if (e.name.toLowerCase().includes(term)) hits.push({ ...e, dirObjectId: `mock:${rel}`, dirPath: rel });
          if (e.kind === "dir" && depth < 3) walk(rel ? `${rel}/${e.name}` : e.name, depth + 1);
        }
      };
      walk("", 0);
      return hits.slice(0, 100);
    },

    async catalogOccurrences(orgId, machineId, dir) {
      await latency();
      const d = need(orgId);
      needMachine(d, machineId);
      const ids = (d.snapshots.get(machineId) ?? []).filter((x) => x.kind === "files" || x.kind === "share").map((x) => x.id);
      return { dirPath: dir.replace(/^mock:/, ""), snapshotIds: ids };
    },

    async createRestore(orgId, req) {
      await latency();
      const d = need(orgId);
      let machine: T.MachineDetail | undefined;
      for (const [mid, snaps] of d.snapshots) {
        if (snaps.some((s) => s.id === req.snapshotId)) machine = d.machines.find((m) => m.id === mid);
      }
      if (!machine) throw new ApiRequestError(404, "snapshot_not_found", "Snapshot não encontrado.");
      const target = req.target.kind === "other_machine" ? d.machines.find((m) => m.id === req.target.machineId) : machine;
      const job: T.Job = {
        id: `${orgId}:job-${newHex(8)}`, kind: "restore", status: "running", machineId: target?.id ?? null, hostname: target?.hostname ?? null,
        startedAt: iso(Date.now()), endedAt: null, bytes: 0, progress: 0.04,
        messageCode: "", databaseId: null,
        message: req.target.kind === "download" ? `Preparando arquivo ZIP com ${req.paths.length} item(ns)` : `Restaurando ${req.paths.length} item(ns) em ${target?.hostname}`,
      };
      d.jobs.unshift(job);
      d.audit.unshift({ id: newHex(8), at: job.startedAt, actor: session?.user.name ?? "—", action: `Iniciou restauração de ${req.paths.length} item(ns)`, actionCode: "", target: machine.hostname, ip: "177.92.14.203" });
      return clone(job);
    },

    async listGroups(orgId) {
      await latency();
      return clone(need(orgId).groups);
    },

    async listPolicies(orgId) {
      await latency();
      return clone(need(orgId).policies);
    },

    async createPolicy(orgId, input) {
      await latency();
      const d = need(orgId);
      const p: T.Policy = { ...clone(input), id: `${orgId}:pol-${newHex(6)}`, machineCount: 0, updatedAt: iso(Date.now()) };
      d.policies.push(p);
      return clone(p);
    },

    async updatePolicy(orgId, policyId, input) {
      await latency();
      const d = need(orgId);
      const p = d.policies.find((x) => x.id === policyId);
      if (!p) throw new ApiRequestError(404, "policy_not_found", "Política não encontrada.");
      Object.assign(p, clone(input), { updatedAt: iso(Date.now()) });
      d.audit.unshift({ id: newHex(8), at: p.updatedAt, actor: session?.user.name ?? "—", action: "Alterou política", actionCode: "", target: p.name, ip: "177.92.14.203" });
      return clone(p);
    },

    async getUsage(orgId) {
      await latency();
      return clone(need(orgId).usage);
    },

    async storage(orgId) {
      await latency();
      need(orgId);
      return storageSummary(orgId);
    },

    // -----------------------------------------------------------------------
    // Administração da plataforma (ADR 0021)

    async adminListPools() {
      await latency();
      needPlatformAdmin();
      return platformPools().map(withTotals);
    },

    async adminCreatePool(input) {
      await latency();
      needPlatformAdmin();
      const pool: T.StoragePool = {
        id: `pool-${newHex(6)}`, name: input.name, backend: input.backend, location: locationOf(input), role: input.role ?? "primary",
        replicaPoolId: null, immutableDays: null, usedBytes: 0, capacityBytes: null, dedupRatio: 1, health: "healthy",
        lastCheckAt: iso(Date.now()), lastError: null, replicationLagSeconds: null, editable: true, settings: clone(input.settings),
        detectedCapacityBytes: null, detectedFreeBytes: null, probedAt: null, assignedQuotaBytes: null, orgCount: 0,
      };
      platformPools().push(pool);
      return withTotals(pool);
    },

    async adminUpdatePool(poolId, input) {
      await latency();
      needPlatformAdmin();
      const pool = needPool(poolId);
      pool.name = input.name;
      pool.settings = clone(input.settings);
      pool.location = locationOf(input);
      if (input.role) pool.role = input.role;
      return withTotals(pool);
    },

    async adminDeletePool(poolId) {
      await latency();
      needPlatformAdmin();
      needPool(poolId);
      if (assignments().some((a) => a.poolId === poolId)) {
        throw new ApiRequestError(409, "pool_in_use", "Este pool está atribuído a alguma organização. Remova as atribuições antes de apagá-lo.");
      }
      const pools = platformPools();
      pools.splice(pools.findIndex((x) => x.id === poolId), 1);
      for (const p of pools) if (p.replicaPoolId === poolId) p.replicaPoolId = null;
    },

    async adminTestPool() {
      await latency();
      needPlatformAdmin();
      return { ok: true };
    },

    async adminTLSCertificate(url) {
      await latency();
      const host = url.replace(/^https?:\/\//, "").split("/")[0] ?? "servidor";
      return { sha256: "3b8f1e6c9a2d4f7081b5c3e9d2a6f4c1e8b7a9d0c2f3e4a5b6c7d8e9f0a1b2c3", subject: host, issuer: host, dnsNames: [host], notBefore: "2025-08-19T13:50:48Z", notAfter: "2026-08-19T13:50:48Z", verifyError: "x509: certificate has expired or is not yet valid" };
    },

    async adminSftpHostKey(host) {
      await latency();
      needPlatformAdmin();
      return { knownHosts: `${host} ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIExemploDeChaveDoServidorMock`, fingerprint: "SHA256:mock3xemplo7Q2mKd81ZpVb4nJ0cW", keyType: "ssh-ed25519" };
    },

    async adminSetPoolReplica(poolId, replicaPoolId) {
      await latency();
      needPlatformAdmin();
      const pool = needPool(poolId);
      if (replicaPoolId !== null && needPool(replicaPoolId).role !== "replica") {
        throw new ApiRequestError(422, "not_a_replica", "O pool escolhido não é de réplica.");
      }
      pool.replicaPoolId = replicaPoolId;
      return withTotals(pool);
    },

    async adminProbePool(poolId) {
      await latency();
      needPlatformAdmin();
      const pool = needPool(poolId);
      // Só disco local e SFTP informam capacidade (statfs, statvfs).
      const measurable = pool.backend === "local" || pool.backend === "sftp";
      const capacity = pool.capacityBytes ?? 60e12;
      pool.detectedCapacityBytes = measurable ? Math.round(capacity * 0.985) : null;
      pool.detectedFreeBytes = measurable ? Math.max(0, Math.round(capacity * 0.985) - poolTotals(poolId).usedBytes) : null;
      pool.probedAt = iso(Date.now());
      return { capacityBytes: pool.capacityBytes, detectedCapacityBytes: pool.detectedCapacityBytes, detectedFreeBytes: pool.detectedFreeBytes, probedAt: pool.probedAt };
    },

    async adminCreateOrg(input) {
      await latency();
      needPlatformAdmin();
      const name = (input.name ?? "").trim();
      if (!name || name.length > 120) throw new ApiRequestError(422, "invalid_org", "Confira o nome da organização (1 a 120 caracteres).");
      if (input.kind !== "msp" && input.kind !== "customer") throw new ApiRequestError(422, "invalid_org_kind", "O tipo deve ser msp ou customer.");
      const org = addOrg(name, input.kind, input.parentId || null, input.storageQuotaBytes ?? null);
      return toOrgDetail(org);
    },

    async adminUpdateOrg(orgId, input) {
      await latency();
      needPlatformAdmin();
      const name = (input.name ?? "").trim();
      if (!name || name.length > 120) throw new ApiRequestError(422, "invalid_org", "Confira o nome da organização (1 a 120 caracteres).");
      if (input.trialEndsAt !== undefined) setTrialEnd(orgId, input.trialEndsAt);
      const org = renameOrg(orgId, name);
      if (!org) throw new ApiRequestError(404, "org_not_found", "Organização não encontrada.");
      return toOrgDetail(org);
    },

    async adminCloseOrg(orgId) {
      await latency();
      needPlatformAdmin();
      const r = closeOrg(orgId, Date.now());
      if (r === null) throw new ApiRequestError(404, "org_not_found", "Organização não encontrada.");
      if (r === "closed") throw new ApiRequestError(409, "org_closed", "A organização já está encerrada.");
      if (r === "children") throw new ApiRequestError(409, "org_has_children", "Este MSP tem organizações filhas. Encerre as filhas antes.");
      return toOrgDetail(r);
    },

    async adminReopenOrg(orgId) {
      await latency();
      needPlatformAdmin();
      const r = reopenOrg(orgId, Date.now());
      if (r === null) throw new ApiRequestError(404, "org_not_found", "Organização não encontrada.");
      if (r === "open") throw new ApiRequestError(409, "org_not_closed", "A organização não está encerrada.");
      return toOrgDetail(r);
    },

    async adminPurgeOrg(orgId) {
      await latency();
      needPlatformAdmin();
      const r = purgeOrg(orgId, Date.now());
      if (r === null) throw new ApiRequestError(404, "org_not_found", "Organização não encontrada.");
      if (r === "open") throw new ApiRequestError(409, "org_not_closed", "A organização não está encerrada.");
      return toOrgDetail(r);
    },

    async adminListOrgPools(orgId) {
      await latency();
      needPlatformAdmin();
      need(orgId);
      return orgPools(orgId);
    },

    async adminSetOrgPool(orgId, poolId, input) {
      await latency();
      needPlatformAdmin();
      need(orgId);
      needPool(poolId);
      if (input.quotaBytes !== null && input.quotaBytes <= 0) throw new ApiRequestError(422, "invalid_quota", "A quota precisa ser maior que zero, ou vazia para sem limite.");
      const existing = assignments().find((a) => a.orgId === orgId && a.poolId === poolId);
      if (existing) existing.quotaBytes = input.quotaBytes;
      else assignments().push({ orgId, poolId, quotaBytes: input.quotaBytes, state: "active" });
      const row = orgPools(orgId).find((x) => x.poolId === poolId);
      if (!row) throw new ApiRequestError(500, "assignment_missing", "Atribuição não gravada.");
      return row;
    },

    async adminRemoveOrgPool(orgId, poolId) {
      await latency();
      needPlatformAdmin();
      need(orgId);
      const row = orgPools(orgId).find((x) => x.poolId === poolId);
      if (!row) throw new ApiRequestError(404, "assignment_not_found", "Este pool não está atribuído a esta organização.");
      // A migração de dados entre pools chega na próxima entrega (ADR 0021).
      if (row.repositories > 0) {
        throw new ApiRequestError(409, "pool_has_data", "Há máquinas desta organização com dados neste pool. A mudança de pool com cópia dos dados chega na próxima entrega.");
      }
      const list = assignments();
      const i = list.findIndex((a) => a.orgId === orgId && a.poolId === poolId);
      if (i >= 0) list.splice(i, 1);
    },

    async listEnrollmentTokens(orgId) {
      await latency();
      return clone(need(orgId).tokens);
    },

    async createEnrollmentToken(orgId, input) {
      await latency();
      const d = need(orgId);
      const now = Date.now();
      const token: T.EnrollmentToken = {
        id: `${orgId}:tok-${newHex(6)}`, label: input.label, groupId: input.groupId ?? "", policyId: input.policyId ?? "",
        createdAt: iso(now), expiresAt: iso(now + input.expiresInHours * 3600_000), uses: 0, maxUses: input.maxUses,
        createdBy: session?.user.name ?? "—",
      };
      d.tokens.unshift(token);
      d.audit.unshift({ id: newHex(8), at: token.createdAt, actor: token.createdBy, action: `Criou token de registro para ${input.maxUses} máquina(s)`, actionCode: "", target: input.label, ip: "177.92.14.203" });
      const short = token.id.split("tok-")[1] ?? "000000";
      const version = "2026.09.1";
      const bundleFileName = `ebk-registro-${short}.json`;
      // Mesmo formato do servidor; "#" marca download simulado na console.
      const pkg = (os: string, arch: "amd64" | "arm64", ext: string, sizeBytes: number): T.AgentPackage => ({
        arch, fileName: `e-backup-agent-${os}-${arch}.${ext}`, downloadUrl: `#e-backup-agent-${os}-${arch}.${ext}`,
        sha256: newHex(32), sizeBytes, version,
      });
      const installer = (packages: T.AgentPackage[], silentCommand: string): T.EnrollmentTokenCreated["installers"]["windows"] => ({
        fileName: bundleFileName, downloadUrl: packages[0]?.downloadUrl ?? null, silentCommand, packages,
      });
      return clone({
        token,
        bundle: JSON.stringify({ v: 1, gateway: "agents.exemplo.com.br:443", token: "ebk1_simulado", ca_pem: "-----BEGIN CERTIFICATE-----\n(simulado)\n-----END CERTIFICATE-----" }, null, 2),
        bundleFileName,
        installers: {
          windows: installer([pkg("windows", "amd64", "zip", 9_328_391), pkg("windows", "arm64", "zip", 8_161_081)],
            `powershell -NoProfile -ExecutionPolicy Bypass -File .\\install.ps1 -Bundle .\\${bundleFileName}`),
          macos: installer([pkg("macos", "arm64", "tar.gz", 8_369_123), pkg("macos", "amd64", "tar.gz", 9_235_910)], `sudo ./install.sh ${bundleFileName}`),
          linux: installer([pkg("linux", "amd64", "tar.gz", 9_050_867), pkg("linux", "arm64", "tar.gz", 8_014_381)], `sudo ./install.sh ${bundleFileName}`),
        },
      });
    },

    async listActivity(orgId, machineId) {
      await latency();
      const d = need(orgId);
      return clone(machineId ? d.jobs.filter((j) => j.machineId === machineId) : d.jobs);
    },

    async listAlerts(orgId, status) {
      await latency();
      return clone(need(orgId).alerts.filter((a) => status === "all" || a.status === "open"));
    },

    async listNotificationChannels(orgId) {
      await latency();
      return { emailAvailable: true, channels: clone(need(orgId).channels) };
    },

    async createNotificationChannel(orgId, input) {
      await latency();
      const d = need(orgId);
      if (d.channels.some((c) => c.name === input.name)) throw new ApiRequestError(409, "channel_name_taken", "Já existe um canal com este nome.");
      const channel: T.NotificationChannel = {
        id: newHex(8), kind: input.kind, name: input.name, emails: input.kind === "email" ? input.emails ?? [] : [],
        url: input.kind === "webhook" ? input.url ?? null : null, minSeverity: input.minSeverity ?? "warning", locale: input.locale ?? "pt-BR",
        enabled: input.enabled ?? true, monthlySummary: input.kind === "email" && (input.monthlySummary ?? false),
        createdAt: iso(Date.now()), lastDeliveryAt: null, lastError: null,
      };
      d.channels.push(channel);
      return { channel: clone(channel), secret: input.kind === "webhook" ? `whsec_${newHex(20)}` : null };
    },

    async updateNotificationChannel(orgId, channelId, input) {
      await latency();
      const c = need(orgId).channels.find((x) => x.id === channelId);
      if (!c) throw new ApiRequestError(404, "channel_not_found", "Canal de notificação não encontrado.");
      Object.assign(c, {
        name: input.name, emails: c.kind === "email" ? input.emails ?? c.emails : [], url: c.kind === "webhook" ? input.url ?? c.url : null,
        minSeverity: input.minSeverity ?? c.minSeverity, locale: input.locale ?? c.locale, enabled: input.enabled ?? c.enabled,
        monthlySummary: c.kind === "email" && (input.monthlySummary ?? false),
      });
      return clone(c);
    },

    async deleteNotificationChannel(orgId, channelId) {
      await latency();
      const d = need(orgId);
      d.channels = d.channels.filter((x) => x.id !== channelId);
    },

    async testNotificationChannel(orgId, channelId) {
      await latency();
      const c = need(orgId).channels.find((x) => x.id === channelId);
      if (!c) throw new ApiRequestError(404, "channel_not_found", "Canal de notificação não encontrado.");
      c.lastDeliveryAt = iso(Date.now());
      c.lastError = null;
      return { ok: true };
    },

    async sendMonthlySummary(orgId, channelId) {
      await latency();
      const c = need(orgId).channels.find((x) => x.id === channelId);
      if (!c) throw new ApiRequestError(404, "channel_not_found", "Canal de notificação não encontrado.");
      if (c.kind !== "email") throw new ApiRequestError(422, "monthly_summary_email_only", "O resumo mensal só vai por canal de e-mail.");
      c.lastDeliveryAt = iso(Date.now());
      c.lastError = null;
      return { ok: true };
    },

    async listReportKinds(orgId) {
      await latency();
      need(orgId);
      return ["summary", "executions", "coverage", "rpo", "restores", "retention", "audit", "access", "alerts"];
    },

    async listIssuedReports(orgId) {
      await latency();
      need(orgId);
      const now = Date.now();
      const issue = (i: number, kind: T.ReportKind, format: "pdf" | "csv", who: string): T.ReportIssue => ({
        id: `rep-${orgId}-${i}`, kind, from: "2026-08-01", to: "2026-08-31", format, locale: "pt-BR",
        sha256: newHex(32), bytes: format === "pdf" ? 184_320 + i * 1024 : 12_288, issuedBy: who, issuedAt: iso(now - i * 3 * DAY),
      });
      return [issue(1, "summary", "pdf", "Rafaela Nunes"), issue(2, "executions", "csv", "Diego Prates"), issue(3, "restores", "pdf", "Rafaela Nunes")];
    },

    async listAudit(orgId) {
      await latency();
      return clone(need(orgId).audit);
    },

    async listUsers(orgId) {
      await latency();
      return clone(need(orgId).users);
    },

    async updateMember(orgId, userId, role) {
      await latency();
      const u = need(orgId).users.find((x) => x.id === userId);
      if (!u) throw new ApiRequestError(404, "member_not_found", "Pessoa não encontrada nesta organização.");
      if (u.id === session?.user.id) throw new ApiRequestError(422, "self_change", "Você não pode alterar nem remover o próprio acesso.");
      u.role = role;
      return clone(u);
    },

    async removeMember(orgId, userId) {
      await latency();
      const d = need(orgId);
      if (userId === session?.user.id) throw new ApiRequestError(422, "self_change", "Você não pode alterar nem remover o próprio acesso.");
      d.users = d.users.filter((x) => x.id !== userId);
    },

    async resetMemberTotp(orgId, userId) {
      await latency();
      const d = need(orgId);
      const s = needSession();
      const u = d.users.find((x) => x.id === userId);
      if (!u) throw new ApiRequestError(404, "member_not_found", "Pessoa não encontrada nesta organização.");
      if (u.id === s.user.id) throw new ApiRequestError(422, "self_change", "Você não pode alterar nem remover o próprio acesso. Peça a outro administrador.");
      if (u.inherited) throw new ApiRequestError(422, "inherited_member", "Este acesso vem do MSP e só pode ser alterado lá.");
      if (u.platformAdmin && !s.user.platformAdmin) throw new ApiRequestError(403, "forbidden", "Você não tem permissão para esta ação.");
      u.totpEnabled = false;
      d.audit.unshift({ id: newHex(8), at: iso(Date.now()), actor: s.user.name, action: "Redefiniu a verificação em duas etapas", actionCode: "", target: u.email, ip: "177.92.14.203" });
    },

    async listInvitations(orgId) {
      await latency();
      return clone(need(orgId).invitations);
    },

    async createInvitation(orgId, input) {
      await latency();
      const now = Date.now();
      const token = `ebi1_${newHex(20)}`;
      const invitation: T.Invitation = {
        id: newHex(8), email: input.email, name: input.name, role: input.role, status: "pending",
        createdBy: session?.user.name ?? "—", createdAt: iso(now), expiresAt: iso(now + 72 * 3600_000),
      };
      need(orgId).invitations.unshift(invitation);
      return { invitation: clone(invitation), token, acceptPath: `/invite#${token}` };
    },

    async revokeInvitation(orgId, id) {
      await latency();
      const inv = need(orgId).invitations.find((x) => x.id === id);
      if (inv) inv.status = "revoked";
    },

    async listApiTokens(orgId) {
      await latency();
      return clone(need(orgId).apiTokens);
    },

    async createApiToken(orgId, input) {
      await latency();
      const now = Date.now();
      const secret = `ebt1_${newHex(21)}`;
      const token: T.ApiToken = {
        id: newHex(8), name: input.name, prefix: secret.slice(0, 11), role: input.role, createdBy: session?.user.name ?? "—",
        createdAt: iso(now), lastUsedAt: null, expiresAt: iso(now + input.expiresInDays * 86_400_000),
      };
      need(orgId).apiTokens.unshift(token);
      return { token: clone(token), secret };
    },

    async revokeApiToken(orgId, id) {
      await latency();
      const d = need(orgId);
      d.apiTokens = d.apiTokens.filter((x) => x.id !== id);
    },
  };
}

export { DAY };
